import {Async} from 'Core/models/Async';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {CaseNumber, UserAssignedCaseNumber, CaseNumberError, matchCaseNumber} from './CaseNumber';
import {NonEmptyString, StringUnion, EmptyString} from 'Core/models/EmptyString';
import {AgentList, EmptyAgentList, ValidAgentList} from './AgentList';

export class UnvalidatedGroupVerifyCriticalInformation {
  public readonly key = 'UnvalidatedGroupVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList,
    public readonly achAccountNumber: StringUnion,
    public readonly achTransitNumber: StringUnion 
  ) {}
}

export class InvalidGroupVerifyCriticalInformation {
  public readonly key = 'InvalidGroupVerifyCriticalInformation';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList,
    public readonly achAccountNumber: StringUnion,
    public readonly achTransitNumber: StringUnion
  ) {}
}

type GroupVerifyCriticalInformationProps = {
  caseNumber: CaseNumber;
  caseName: StringUnion;
  applicant: StringUnion;
  controlNumber: StringUnion;
  agentList: AgentList;
  achAccountNumber: StringUnion;
  achTransitNumber: StringUnion
};

export class ValidGroupVerifyCriticalInformation {
  public static createFromApp({
    caseNumber,
    caseName,
    controlNumber,
    applicant,
    agentList,
    achAccountNumber,
    achTransitNumber
  }: GroupVerifyCriticalInformationProps) {
    return this.create(caseNumber, caseName, controlNumber, applicant, agentList, achAccountNumber, achTransitNumber);
  }

  public static create(
    caseNumber: CaseNumber,
    caseName: StringUnion,
    controlNumber: StringUnion,
    applicant: StringUnion,
    agentList: AgentList,
    achAccountNumber: StringUnion,
    achTransitNumber: StringUnion
  ) {
    return new ValidGroupVerifyCriticalInformation(caseNumber, caseName, controlNumber, applicant, agentList, achAccountNumber, achTransitNumber);
  }

  public readonly key = 'ValidGroupVerifyCriticalInformation';
  private constructor(
    public readonly caseNumber: CaseNumber,
    public readonly caseName: StringUnion,
    public readonly controlNumber: StringUnion,
    public readonly applicant: StringUnion,
    public readonly agentList: AgentList,
    public readonly achAccountNumber: StringUnion,
    public readonly achTransitNumber: StringUnion
  ) {}
}

export type GroupVerifyCriticalInformation =
  | UnvalidatedGroupVerifyCriticalInformation
  | InvalidGroupVerifyCriticalInformation
  | ValidGroupVerifyCriticalInformation;

export type GroupVerifyCriticalInformationOptions<TReturn> = ToMatchOptions<
  GroupVerifyCriticalInformation,
  'key',
  TReturn
>;

export const matchGroupVerifyCriticalInformation = <TReturn>(
  options: GroupVerifyCriticalInformationOptions<TReturn>
) => (app: GroupVerifyCriticalInformation): TReturn => {
  switch (app.key) {
    case 'InvalidGroupVerifyCriticalInformation':
      return options.InvalidGroupVerifyCriticalInformation(app);
    case 'UnvalidatedGroupVerifyCriticalInformation':
      return options.UnvalidatedGroupVerifyCriticalInformation(app);
    case 'ValidGroupVerifyCriticalInformation':
      return options.ValidGroupVerifyCriticalInformation(app);
  }
};

export const changeCaseNumber = (caseNumber: Async<string>) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber,
  });

export const changeUserCaseNumber = (caseNumber: string) => (app: GroupVerifyCriticalInformation) => {
  return ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: UserAssignedCaseNumber.create(caseNumber),
  });
};

export const changeCaseNumberError = (caseNumber: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    caseNumber: CaseNumberError.create(caseNumber),
  });

export const changeCaseName = (caseName: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    caseName: NonEmptyString.create(caseName),
  });

export const changeApplicant = (applicant: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    applicant: NonEmptyString.create(applicant),
  });

export const changeControlNumber = (controlNumber: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeAgentList = (agentList: AgentList) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
    ...app,
    agentList: ValidAgentList.create(agentList.agentList),
  });

// export const changeTransit = (transit: string) => (app: GroupVerifyCriticalInformation) =>
//   ValidGroupVerifyCriticalInformation.createFromApp({
//   ...app,
//   transit: NonEmptyString.create(transit),
// });

export const changeAchTransitNumber = (achTransitNumber: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
  ...app,
  achTransitNumber: NonEmptyString.create(achTransitNumber),
});

export const changeAchAccountNumber = (achAccountNumber: string) => (app: GroupVerifyCriticalInformation) =>
  ValidGroupVerifyCriticalInformation.createFromApp({
  ...app,
  achAccountNumber: NonEmptyString.create(achAccountNumber),
});

export const EmptyGroupVerifyCriticalInformation = new UnvalidatedGroupVerifyCriticalInformation(
  new UserAssignedCaseNumber(''),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyAgentList,
  new EmptyString(),
  new EmptyString()
);

export const isVerified = (
  originalGroupVerifyCriticalInformation: GroupVerifyCriticalInformation,
  groupVerifyCriticalInformation: GroupVerifyCriticalInformation
): boolean => {
  const getCaseNumber = (caseNumber: CaseNumber) =>
    matchCaseNumber({
      Loading: () => '',
      Loaded: caseNumber => caseNumber,
      Unassigned: () => '',
      UserAssignedCaseNumber: caseNumber => caseNumber,
      Error: () => '',
      CaseNumberError: () => '',
    })(caseNumber)!;

  const {agentList, caseNumber, controlNumber, applicant, achAccountNumber, achTransitNumber} = originalGroupVerifyCriticalInformation;
  const {
    agentList: newAgentList,
    applicant: newApplicant,
    caseNumber: newCaseNumber,
    controlNumber: newControlNumber,
    achAccountNumber: newAccountNumber,
    achTransitNumber: newTransit,
  } = groupVerifyCriticalInformation;

  return (
    agentList.agentList.size > 0 &&
    agentList.agentList.count(agentInformation => newAgentList.agentList.toArray().includes(agentInformation)) ===
      agentList.agentList.size &&
    getCaseNumber(caseNumber) !== '' &&
    controlNumber.value !== '' &&
    getCaseNumber(caseNumber).toLowerCase() === getCaseNumber(newCaseNumber).toLowerCase() &&
    controlNumber.value === newControlNumber.value &&
    achAccountNumber.value === newAccountNumber.value &&
    achTransitNumber.value === newTransit.value &&
    applicant.value !== '' &&
    applicant.value.toLowerCase() === newApplicant.value.toLowerCase()
  );
};

export const isValidGroupVerifyCriticalInformation = (
  groupVerifyCriticalInformation: GroupVerifyCriticalInformation
): groupVerifyCriticalInformation is ValidGroupVerifyCriticalInformation => {
  return groupVerifyCriticalInformation.key === 'ValidGroupVerifyCriticalInformation';
};
